import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Provider } from 'react-redux';
import { store } from './redux/store';
import { SessionProvider } from './context/SessionContext';
import { ShareProvider } from './context/ShareContext';
import AmbassadorLandingPage from './components/landing/AmbassadorLandingPage';
import SessionExpiredModal from './components/common/SessionExpiredModal';
import SubscriptionManager from './components/common/SubscriptionManager';
import './App.css';
// Home page now shows AmbassadorLandingPage; MyCloset lives at /my-closet
import MyCloset from './components/my closet page/MyCloset';
// Removed LookBook import as it's now integrated into MyCloset page
import UploadClothes from './components/upload clothes page/UploadClothes';
import AuthCallback from './components/auth/AuthCallback';
import 'semantic-ui-css/semantic.min.css'
import SharePreview from './components/share preview page/SharePreview';

import { useViewportHeight } from './hooks/useViewportHeight';
import { fetchLaunchConfig } from './services/launchConfigService';

import { BACKEND_URL, API_ENDPOINTS } from './utils/config';
import { useShareFlow } from './hooks/useShareFlow';
import SharedItemModal from './components/common/SharedItemModal';
import ShareLoginPrompt from './components/common/ShareLoginPrompt';
import ClipboardSharePrompt from './components/common/ClipboardSharePrompt';
import ShareBundleModal from './components/share-bundle/ShareBundleModal';
import ProfileShareModal from './components/profile-share/ProfileShareModal';
import useExtensionSync from './hooks/useExtensionSync';
import ContactPage from './components/contact/ContactPage';
import PrivacyPolicy from './components/privacy/PrivacyPolicy';
import PersonaClosetPage from './components/persona-closet/PersonaClosetPage';

// Wrapper component to check auth status and handle SSO
function MyClosetWrapper() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [selectedClothingId, setSelectedClothingId] = useState(null);
  const [selectVtoId, setSelectVtoId] = useState(null);

  useEffect(() => {
    const handleAuth = async () => {
      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get('token');
      const selectedClothing = searchParams.get('selectedClothing');
      const selectVto = searchParams.get('selectVto');

      // Preserve selectedClothing param for scroll-to behavior
      if (selectedClothing) {
        setSelectedClothingId(selectedClothing);
      }

      // Preserve selectVto param for auto-selecting a VTO result in bottom panel
      if (selectVto) {
        setSelectVtoId(selectVto);
      }

      // Persist share token to localStorage (useShareFlow picks it up after auth)
      const shareToken = searchParams.get('share');
      if (shareToken) {
        localStorage.setItem('pending_share_token', shareToken);
      }

      // A creator's share link carries their referral code. Keep it until the
      // visitor signs up, under the same key SHEYOU's register page reads, so
      // the person who brought them in is credited whichever app they join on.
      const ref = searchParams.get('ref');
      if (ref && /^[A-Za-z0-9_-]{3,32}$/.test(ref)) {
        localStorage.setItem('pendingReferralCode', ref);
      }

      if (token) {
        try {
          // Verify token and get user info
          const response = await fetch(`${BACKEND_URL}${API_ENDPOINTS.AUTH.ME}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const userData = await response.json();
            console.log("SSO Login Successful:", userData.username);

            localStorage.setItem('token', token);
            localStorage.setItem('user', userData.id);
            localStorage.setItem('username', userData.username);

            // Clean URL (selectedClothing already saved in state)
            const newSearchParams = new URLSearchParams();
            if (searchParams.get('view')) {
                newSearchParams.set('view', searchParams.get('view'));
            }
            navigate({ pathname: '/my-closet', search: newSearchParams.toString() }, { replace: true });
            return;
          } else {
            console.error('SSO Token validation failed');
          }
        } catch (error) {
          console.error('SSO Error:', error);
        }
      }
      setIsChecking(false);
    };

    handleAuth();
  }, [location.search, navigate]);

  // Share flow: detect pending share token + claim after auth
  // Must be called before any early returns (rules of hooks)
  const {
    shareModalData,
    closeShareModal,
    hasPendingShare,
    pendingShareInfo,
    dismissPendingShare,
    clipboardShare,
    acceptClipboardShare,
    dismissClipboardShare,
    profileShare,
    closeProfileShare
  } = useShareFlow();

  // Listen for browser extension saves → auto-refresh closet data
  useExtensionSync();

  if (isChecking) {
    return (
      <div className="ui active dimmer">
        <div className="ui loader">Loading your closet...</div>
      </div>
    );
  }

  const isLoggedIn = !!localStorage.getItem('token');

  return (
    <>
      <MyCloset guest={!isLoggedIn} scrollToClothingId={selectedClothingId} selectVtoId={selectVtoId} />
      <SharedItemModal
        isOpen={!!shareModalData}
        onClose={closeShareModal}
        sharerName={shareModalData?.sharerName}
        items={shareModalData?.items || []}
        title={shareModalData?.title}
        sourceType={shareModalData?.sourceType}
        closet={shareModalData?.closet}
      />
      <ShareLoginPrompt
        isOpen={hasPendingShare}
        onDismiss={dismissPendingShare}
        shareInfo={pendingShareInfo}
      />
      {/* Shared profile link — viewing only, nothing enters the closet */}
      <ProfileShareModal
        isOpen={!!profileShare}
        profile={profileShare}
        onClose={closeProfileShare}
      />
      {/* Share link found on the clipboard rather than opened as a URL */}
      <ClipboardSharePrompt
        isOpen={!!clipboardShare}
        share={clipboardShare}
        onAccept={acceptClipboardShare}
        onDismiss={dismissClipboardShare}
      />
    </>
  );
}

function App() {
  useViewportHeight();

  useEffect(() => {
    fetchLaunchConfig().then(config => {
      console.log('Launch Config loaded:', config);
    });
  }, []);

  return (
    <Provider store={store}>
      <SessionProvider>
        <ShareProvider>
          <Router>
            <div className="App">
              <SessionExpiredModal />
              <SubscriptionManager />
              <Routes>
                <Route exact path="/" element={<AmbassadorLandingPage />} />
                <Route exact path="/my-closet" element={<MyClosetWrapper />} />
                <Route exact path="/guest-closet" element={<Navigate to="/my-closet" replace />} />
                <Route path="/closet/:username" element={<PersonaClosetPage />} />
                {/* LookBook route removed */}
                {/* sign up/login handled by inline VTOLoginConfirmationPortal modal */}
                <Route exact path="/upload-clothes" element={<UploadClothes />} />
                <Route exact path="/edit-clothes" element={<UploadClothes edit={true} />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/share-preview" element={<SharePreview />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />

              </Routes>
              {/* Mounted once — any card can open it via useShare().openShare() */}
              <ShareBundleModal />
            </div>
          </Router>
        </ShareProvider>
      </SessionProvider>
    </Provider>
  );
}

export default App;
